import Text from 'components/Text';
import { TEXT_STRING } from '../../constants';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import Select from 'components/Select';
import DatePicker from 'components/DatePicker';
import TimePicker from 'components/TimePicker';
import { EProjectType, TMaleDetail, TProjectInfo, TRecommendFemale } from 'types';
import moment, { Moment } from 'moment';
import Label from 'components/Label';
import OrderFooter from './OrderFooter';
import { FIELD_NAME } from 'helpers';
import SearchFemale from './SearchFemale';
import InvitedMalesCards from './InvitedMalesCards';

const STR = TEXT_STRING.CALL_WOMAN;
const COMMON_STR = TEXT_STRING.COMMON;
const ORDER_STR = TEXT_STRING.ORDER_DETAIL;

export interface CallWomanRef {
  handleValidate: () => boolean;
}

type Props = {
  projectInfo: TProjectInfo | undefined;
  estimatePrice: string;
  formState: Record<string, string>;
  setFormState: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  listSelectedGirl: TRecommendFemale[];
  setListSelectedGirl: React.Dispatch<React.SetStateAction<TRecommendFemale[]>>;
  invitedMaleRoster?: TMaleDetail[];
  activeInvitedMaleIds?: number[];
  onToggleInvitedMale?: (maleId: number) => void;
  onOrder: () => void;
  textFooterTitle?: string;
  textFooterButton?: string;
  isCallNewGirl?: boolean;
  loading?: boolean;
  fixUserId?: number;
};

const CallWoman = forwardRef<CallWomanRef, Props>(
  (
    {
      projectInfo,
      formState,
      setFormState,
      estimatePrice,
      listSelectedGirl,
      setListSelectedGirl,
      invitedMaleRoster = [],
      activeInvitedMaleIds = [],
      onToggleInvitedMale = () => {},
      onOrder,
      textFooterTitle = STR.ESTIMATED_AMOUNT,
      textFooterButton = STR.ENTER_DETAIL,
      isCallNewGirl = false,
      loading = false,
      fixUserId,
    },
    ref,
  ) => {
    // State
    const [errorFields, setErrorFields] = useState<string[]>([]);
    const [errorMessage, setErrorMessageTime] = useState<string>('');

    // Memo & Callbacks
    const optionSelectGirl = useMemo(() => {
      const maleCount = +formState[FIELD_NAME.MALE_COUNT];
      let filteredOptions = (projectInfo?.optionsPeople || []).map(
        (option) => ({
          ...option,
        }),
      );

      if (
        maleCount > 1 &&
        formState[FIELD_NAME.ORDER_TYPE] === EProjectType.BASIC
      ) {
        let singleOption = filteredOptions?.find(
          (option) => +option.value === 1,
        );
        if (singleOption) {
          singleOption.disabled = true;
        }
      }

      return filteredOptions;
    }, [formState, projectInfo?.optionsPeople]);

    const clearListGirl = useCallback(() => {
      setListSelectedGirl((prev) =>
        fixUserId ? prev.filter((girl) => girl.id === fixUserId) : [],
      );
    }, [setListSelectedGirl, fixUserId]);

    const handleChange = useCallback(
      (name: string, value: string) => {
        if (value) {
          setErrorFields((prev) => prev.filter((field) => field !== name));
          setErrorMessageTime('');
        }
        setFormState((prev) => ({ ...prev, [name]: value }));
      },
      [setFormState],
    );

    const isValidDate = useCallback((currentDate: Moment) => {
      const yesterday = moment().subtract(1, 'day');
      return currentDate.isAfter(yesterday);
    }, []);

    const validateDateTime = useCallback((): {
      valid: boolean;
      message: string;
    } => {
      const dateStr = formState[FIELD_NAME.DATE];
      const timeStr = formState[FIELD_NAME.TIME];

      if (!dateStr || !timeStr) {
        return { valid: false, message: STR.DATE_WARNING };
      }

      const date = moment(dateStr);
      const time = moment(timeStr, 'HH:mm', true);

      if (!date.isValid() || !time.isValid()) {
        return { valid: false, message: STR.DATE_WARNING };
      }

      const dateTime = moment(date).set({
        hour: time.hour(),
        minute: time.minute(),
        second: 0,
        millisecond: 0,
      });

      const now = moment();

      if (!dateTime.isAfter(now)) {
        return { valid: false, message: STR.DATE_WARNING };
      }

      return { valid: true, message: '' };
    }, [formState]);

    const handleValidate = useCallback(() => {
      const { valid, message } = validateDateTime();

      if (!valid) {
        setErrorMessageTime(message);
        setErrorFields([FIELD_NAME.DATE]);
      } else {
        setErrorMessageTime('');
        setErrorFields([]);
      }

      return valid;
    }, [validateDateTime]);

    useImperativeHandle(ref, () => ({
      handleValidate,
    }));

    const handleChoosePeople = useCallback(
      (value: string) => {
        if (+value < listSelectedGirl.length) {
          clearListGirl();
        }
        setFormState((prev) => ({ ...prev, [FIELD_NAME.PEOPLE]: value }));
      },
      [listSelectedGirl.length, setFormState, clearListGirl],
    );

    useEffect(() => {
      const maleCount = +formState[FIELD_NAME.MALE_COUNT];
      const peopleCount = +formState[FIELD_NAME.PEOPLE];
      if (
        formState.orderType === EProjectType.BASIC &&
        peopleCount === 1 &&
        maleCount > peopleCount
      ) {
        setFormState((prev) => ({
          ...prev,
          [FIELD_NAME.PEOPLE]: prev[FIELD_NAME.MALE_COUNT],
        }));
      }
    }, [formState, setFormState]);

    useEffect(() => {
      const inviteCount = activeInvitedMaleIds.length;
      if (!inviteCount) {
        return;
      }
      const expected = inviteCount + 1;
      const maleCount = Number(formState[FIELD_NAME.MALE_COUNT] || 0);
      if (maleCount < expected) {
        setFormState((prev) => ({
          ...prev,
          [FIELD_NAME.MALE_COUNT]: String(expected),
        }));
      }
    }, [formState, activeInvitedMaleIds.length, setFormState]);

    useEffect(() => {
      setFormState((prev) => {
        const prevPeople = Number(prev[FIELD_NAME.PEOPLE] || 0);

        if (listSelectedGirl.length >= prevPeople) {
          return {
            ...prev,
            [FIELD_NAME.PEOPLE]: listSelectedGirl.length.toString(),
          };
        }

        return prev;
      });
    }, [listSelectedGirl.length, setFormState]);

    return (
      <>
        <div className="flex flex-col gap-5">
          {!isCallNewGirl && (
            <>
              {/* TODO: Waiting for release date confirmation*/}
              {/* <div>
              <Label bold require textSize="text-sm">
                {ORDER_STR.HOW_TO_PLAY}
              </Label>
              <div className="grid grid-cols-2 gap-4">
                {projectInfo?.optionsType.map((type) => {
                  const isOrderTypeChosen =
                    formState[FIELD_NAME.ORDER_TYPE] === type.value;
                  return (
                    <RadioButton
                      key={type.name}
                      containerClassName={`w-full border border-neutral-300 p-3 rounded-lg ${
                        isOrderTypeChosen ? 'border-primary bg-primary-100' : ''
                      }`}
                      label={type.name}
                      value={type.value}
                      onChecked={(value) =>
                        handleChange(
                          FIELD_NAME.ORDER_TYPE,
                          value as EProjectType
                        )
                      }
                      isChecked={isOrderTypeChosen}
                    />
                  );
                })}
              </div>
            </div> */}
              <Select
                label={ORDER_STR.NUMBER_CUSTOMER}
                name={FIELD_NAME.MALE_COUNT}
                value={formState[FIELD_NAME.MALE_COUNT]}
                onChange={(value) => {
                  handleChange(FIELD_NAME.MALE_COUNT, value);
                }}
                options={projectInfo?.optionsPeople ?? []}
                require
                bold
                textSize="text-sm"
              />
              {invitedMaleRoster.length > 0 ? (
                <InvitedMalesCards
                  roster={invitedMaleRoster}
                  activeInvitedMaleIds={activeInvitedMaleIds}
                  onToggleInvitedMale={onToggleInvitedMale}
                />
              ) : null}
            </>
          )}
          <div>
            <Label bold className="text-sm leading-8">
              <div className="flex flex-wrap">
                {STR.OPTION_SET}
                {STR.OPTION_PEOPLE}
              </div>
            </Label>
            <div className="flex justify-between mt-2px">
              <div className={`flex-1 pr-6px`}>
                <Select
                  name={FIELD_NAME.SET}
                  value={formState[FIELD_NAME.SET]}
                  onChange={(value) => {
                    handleChange(FIELD_NAME.SET, value);
                  }}
                  options={projectInfo?.optionsSet ?? []}
                />
              </div>
              <div className="w-1/4 pr-6px">
                <Select
                  name={FIELD_NAME.PEOPLE}
                  value={formState[FIELD_NAME.PEOPLE]}
                  onChange={(value) => {
                    handleChoosePeople(value);
                  }}
                  options={optionSelectGirl}
                />
              </div>
            </div>
            <Text textColor="text-error" bold fontSize={12}>
              {STR.ABOUT_AUTO_EXTENSION}
            </Text>
          </div>
          <div>
            <div className="flex items-center mb-8px gap-4">
              <Label
                className="text-[14px] leading-8"
                require={true}
                error={
                  errorFields.includes(FIELD_NAME.TIME) ||
                  errorFields.includes(FIELD_NAME.DATE)
                }
                bold
              >
                {STR.START_TIME}
              </Label>
              <span className="text-error font-bold">{errorMessage}</span>
            </div>
            <div className="flex items-center gap-8px">
              <div className="w-[70%]">
                <DatePicker
                  isValidDate={isValidDate}
                  value={new Date(formState[FIELD_NAME.DATE])}
                  onChange={(date) =>
                    handleChange(FIELD_NAME.DATE, date.toString())
                  }
                />
              </div>
              <div className="w-[30%] relative">
                <TimePicker
                  name={FIELD_NAME.TIME}
                  value={formState[FIELD_NAME.TIME]}
                  onChange={(value) => {
                    handleChange(FIELD_NAME.TIME, value);
                  }}
                  error={errorFields.includes(FIELD_NAME.TIME)}
                />
              </div>
            </div>
          </div>
          <SearchFemale
            listSelectedGirl={listSelectedGirl}
            setListSelectedGirl={setListSelectedGirl}
            fixUserId={fixUserId}
          />
        </div>
        <OrderFooter
          onSubmit={onOrder}
          textTitle={textFooterTitle}
          textButton={textFooterButton}
          loading={loading}
          estimatePrice={
            <>
              <Text fontSize={14}>
                {estimatePrice}
                {COMMON_STR.YEN}
              </Text>
              <Text textColor="text-neutral" fontSize={14}>
                + {STR.GAME_FEE}
              </Text>
            </>
          }
        />
      </>
    );
  },
);

export default CallWoman;
